import { api } from './api';

export type A2aSessionResponse = {
  authenticated: boolean;
  email: string | null;
  /** Per-customer flag: this account's loyalty calls go to MAM Engine (limits come from the engine). */
  mamEngine: boolean;
  /** Loyalty profile `data.id`. Null when the visitor has no Alps2Alps session. */
  userId: number | null;
};

export type A2aBalanceResponse = {
  authenticated: boolean;
  /** true when the balance and limits come from MAM Engine, false for the legacy A2A wallet. */
  engine: boolean;
  memberExists: boolean;
  balance: number | null;
  currency: string | null;
  /** Engine only: the maximum that may be paid with MAM on this order; null on the legacy path. */
  maxRedeemable: number | null;
  /** Engine only: why nothing can be redeemed (promo_conflict, below_min_order, member_not_found, ...). */
  blockedBy: string | null;
  minOrderValue: number | null;
};

export type A2aLoginResponse = {
  success: boolean;
  mamEngine?: boolean;
  /** Loyalty profile `data.id` after login or SSO token resolve. */
  userId?: number | null;
  error?: string;
};

export type A2aPayResponse = {
  success: boolean;
  stripeUrl?: string;
  /** Engine path: MAM covered the whole order; the order is paid without a card step. */
  paidInFull?: boolean;
  appliedMam?: number;
  moneyToCharge?: number;
  error?: string;
};

export type A2aAttachOrderResponse = {
  success: boolean;
  mamEngineEnabled?: boolean;
  email?: string;
  error?: string;
};

export type A2aReleaseResponse = {
  success: boolean;
  released?: boolean;
  error?: string;
};

export const a2aService = {
  getSession: () => api.get<A2aSessionResponse>('/api/a2a/session'),

  getBalance: (orderId?: number) =>
    api.get<A2aBalanceResponse>(
      orderId != null ? `/api/a2a/get-wallet?orderId=${String(orderId)}` : '/api/a2a/get-wallet',
    ),

  login: (email: string, password: string) =>
    api.post<A2aLoginResponse>('/api/a2a/login', { email, password }),

  resolveToken: (token: string) =>
    api.post<A2aLoginResponse>('/api/a2a/resolve-token', { token }),

  /** Record the logged-in A2A account on the checkout order (so a paid order can earn MAM). */
  attachOrder: (orderId: number, checkoutToken: string) =>
    api.post<A2aAttachOrderResponse>('/api/a2a/attach-order', { orderId, checkoutToken }),

  pay: (orderId: number, checkoutToken: string, mamAmount: number) =>
    api.post<A2aPayResponse>('/api/a2a/write-off-mam', { orderId, checkoutToken, mamAmount }),

  /** Return MAM redeemed on MAM Engine for an order that will not be paid in this attempt. */
  release: (orderId: number, checkoutToken: string) =>
    api.post<A2aReleaseResponse>('/api/a2a/release-mam', { orderId, checkoutToken }),

  logout: () => api.post<{ success: boolean }>('/api/a2a/logout', {}),
};
